/**
 * Helper utilities for parsing and building observation attempt review events
 * across all actors (Contractor Checker, Internal ADL Checker).
 */

export function parseRejectionHistory(raw) {
  if (Array.isArray(raw)) return raw;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Checks if the current active attempt has a completed Contractor Checker review.
 */
export function hasCurrentAttemptContractorReview(currentAttempt) {
  if (!currentAttempt) return false;
  const isContractorRoute =
    currentAttempt?.submission_route === "CONTRACTOR_CHECKER";
  const hasReviewer = Boolean(currentAttempt?.checker_id || currentAttempt?.checker_name);
  const hasReviewedAt = Boolean(currentAttempt?.checker_reviewed_at);
  const decision = String(currentAttempt?.checker_decision || "").trim().toUpperCase();
  const hasValidDecision = ["APPROVED", "REJECTED"].includes(decision);

  return isContractorRoute && hasReviewer && hasReviewedAt && hasValidDecision;
}

/**
 * Builds review events for an attempt in strict chronological order.
 * An attempt may contain:
 * 1. Contractor Checker review (APPROVED or REJECTED)
 * 2. Internal ADL Checker rejection (if Contractor Checker approved, but Internal Checker subsequently rejected)
 * 3. Direct Internal Checker review (when submission_route != CONTRACTOR_CHECKER)
 *
 * @param {Object} attempt - Attempt model from observation
 * @param {Array|string} rawRejectionHistory - observation.rejection_history
 * @param {Object} detail - parent observation detail
 * @returns {Array} List of review event objects
 */
export function buildAttemptReviewEvents(attempt, rawRejectionHistory = [], detail = null) {
  if (!attempt) return [];

  const attemptNo = Number(attempt.attempt_no || 0);
  const rejectionHistory = parseRejectionHistory(rawRejectionHistory);
  const events = [];

  const isContractorRoute = attempt.submission_route === "CONTRACTOR_CHECKER";
  const attemptDecision = String(attempt.checker_decision || "").trim().toUpperCase();
  const attemptRejectPhotos = Array.isArray(attempt.checker_reject_photographs)
    ? attempt.checker_reject_photographs
    : [];

  if (isContractorRoute) {
    // -------------------------------------------------------------
    // Event 1: Contractor Checker Review
    // -------------------------------------------------------------
    const hasContractorReview =
      Boolean(attempt.checker_name) &&
      (Boolean(attempt.checker_reviewed_at) || ["APPROVED", "REJECTED"].includes(attemptDecision));

    if (hasContractorReview) {
      const isApproved = attemptDecision === "APPROVED";
      const isRejected = attemptDecision === "REJECTED";

      events.push({
        id: `attempt-${attemptNo}-contractor-checker`,
        attemptNo,
        role: "CONTRACTOR_CHECKER",
        reviewerRole: "Contractor Checker",
        reviewerName: attempt.checker_name,
        decision: isApproved ? "APPROVED / FORWARDED" : isRejected ? "REJECTED" : attemptDecision || "REVIEWED",
        rawDecision: attemptDecision || "REVIEWED",
        remarks: attempt.checker_reason || "",
        reviewedAt: attempt.checker_reviewed_at,
        signature: attempt.checker_signature || null,
        // If Contractor Checker rejected, the photos on this attempt belong to them
        rejectionPhotos: isRejected ? attemptRejectPhotos : [],
      });
    }

    // -------------------------------------------------------------
    // Event 2: Internal ADL Checker Rejection (if applicable)
    // -------------------------------------------------------------
    const adlRejection = rejectionHistory.find(
      (entry) =>
        Number(entry?.attempt_no || 0) === attemptNo &&
        String(entry?.checker_level || "").toUpperCase() === "CHECKER"
    );

    if (adlRejection) {
      events.push({
        id: `attempt-${attemptNo}-adl-rejection`,
        attemptNo,
        role: "CHECKER",
        reviewerRole: "ADL Checker Final Review",
        reviewerName:
          adlRejection.checker_name ||
          (detail?.checker_name && detail.checker_name !== attempt.checker_name
            ? detail.checker_name
            : "ADL Safety Checker"),
        decision: "REJECTED",
        rawDecision: "REJECTED",
        remarks: adlRejection.reason || adlRejection.remarks || "",
        reviewedAt: adlRejection.reviewed_at || adlRejection.timestamp,
        signature:
          adlRejection.checker_signature ||
          (Number(detail?.current_attempt_no) === attemptNo ? detail?.checker_signature : null),
        // If Contractor approved, any rejection photos on this attempt belong to the rejecting ADL Checker
        rejectionPhotos: attemptDecision === "APPROVED" ? attemptRejectPhotos : [],
      });
    } else if (
      (detail?.status === "APPROVED" || detail?.status === "CLOSED") &&
      attemptNo === Number(detail?.current_attempt_no || 1) &&
      detail?.checker_name &&
      detail?.checker_name !== attempt.checker_name
    ) {
      events.push({
        id: `attempt-${attemptNo}-adl-approval`,
        attemptNo,
        role: "CHECKER",
        reviewerRole: "ADL Checker Final Review",
        reviewerName: detail.checker_name,
        decision: "APPROVED",
        rawDecision: "APPROVED",
        remarks: detail.checker_remarks || "",
        reviewedAt: detail.checker_reviewed_at || detail.approved_at,
        signature: detail.checker_signature || null,
        rejectionPhotos: [],
      });
    }
  } else {
    // -------------------------------------------------------------
    // Direct Route: No Contractor Checker involved
    // -------------------------------------------------------------
    const matchingRejection = rejectionHistory.find(
      (entry) => Number(entry?.attempt_no || 0) === attemptNo
    );

    if (attemptDecision === "APPROVED") {
      events.push({
        id: `attempt-${attemptNo}-adl-approval`,
        attemptNo,
        role: "CHECKER",
        reviewerRole: "ADL Checker Final Review",
        reviewerName: attempt.checker_name || detail?.checker_name || "ADL Safety Checker",
        decision: "APPROVED",
        rawDecision: "APPROVED",
        remarks: attempt.checker_reason || detail?.checker_remarks || "",
        reviewedAt: attempt.checker_reviewed_at || detail?.approved_at,
        signature: attempt.checker_signature || detail?.checker_signature || null,
        rejectionPhotos: [],
      });
    } else if (attemptDecision === "REJECTED" || matchingRejection || attemptRejectPhotos.length > 0) {
      events.push({
        id: `attempt-${attemptNo}-adl-rejection`,
        attemptNo,
        role: "CHECKER",
        reviewerRole: "ADL Checker Final Review",
        reviewerName:
          matchingRejection?.checker_name ||
          attempt.checker_name ||
          detail?.checker_name ||
          "ADL Safety Checker",
        decision: "REJECTED",
        rawDecision: "REJECTED",
        remarks: matchingRejection?.reason || attempt.checker_reason || "",
        reviewedAt:
          matchingRejection?.reviewed_at ||
          matchingRejection?.timestamp ||
          attempt.checker_reviewed_at,
        signature:
          matchingRejection?.checker_signature ||
          attempt.checker_signature ||
          (Number(detail?.current_attempt_no) === attemptNo ? detail?.checker_signature : null),
        rejectionPhotos: attemptRejectPhotos,
      });
    }
  }

  return events;
}
